import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { LicenseFulfillmentDto } from '../services/licenseComplianceService';
import type { Course } from '../services/courseService';
import { classifyCourseDeliveryMethod } from '../utils/deliveryMethods';
import { HINT_THRESHOLD, findMostConstrainedLimit, getFulfillmentMemo } from '../utils/creditLimitHint';
import { useUserContext } from '../hooks/useUserContext';

interface CourseCreditLimitHintProps {
  course: Pick<Course, 'courseType' | 'meetingFormat' | 'meetingPresentationFormat' | 'courseCredits'>;
  /** Spacing etc. at the mount site — only applied when the hint actually renders. */
  sx?: SxProps<Theme>;
}

/**
 * Course-detail hint: warns when this course's delivery method or fields of
 * study are at or near a credit limit on one of the user's licenses, so they
 * know before registering that some hours may not count. Informational only —
 * renders nothing unless a matching cap is at least 80% used, and fails
 * silently on any fetch error.
 */
export default function CourseCreditLimitHint({ course, sx }: CourseCreditLimitHintProps) {
  const { isSignedIn, clerkUser } = useUserContext();
  // The memo is keyed by user id so a cached fetch is never served across a
  // sign-out/sign-in (or Clerk account switch) to a different user.
  const userId = clerkUser?.id ?? null;
  const [licenses, setLicenses] = useState<LicenseFulfillmentDto[] | null>(null);

  useEffect(() => {
    setLicenses(null); // drop any previous user's data before (re)fetching
    if (!isSignedIn || !userId) return;
    let cancelled = false;
    getFulfillmentMemo(userId)
      .then((data) => {
        if (!cancelled) setLicenses(data.licenses);
      })
      .catch(() => {
        // A hint is never worth an error state.
      });
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, userId]);

  // An unclassifiable delivery method (null) only exempts the course from
  // delivery caps — a field-of-study cap can still match its credit categories.
  const deliveryMethod = classifyCourseDeliveryMethod(course);
  const educationCategoryIds = [
    ...new Set((course.courseCredits ?? []).map((c) => c.educationCategoryId)),
  ];
  const match =
    isSignedIn && licenses
      ? findMostConstrainedLimit(licenses, { deliveryMethod, educationCategoryIds })
      : null;

  if (!match || match.ratio < HINT_THRESHOLD) return null;

  const stateLabel = match.license.stateName || match.license.stateCode;
  const reached = match.ratio >= 1;

  return (
    <Box
      sx={[
        {
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 1,
          borderRadius: 1,
          bgcolor: 'status.modified.bg',
          color: 'status.modified.fg',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <InfoOutlinedIcon fontSize="small" sx={{ mt: '1px' }} />
      <Typography variant="body2">
        {reached
          ? `You've reached your ${match.label} credit limit for ${stateLabel} (${match.used.toFixed(1)} of ${match.cap} hrs used). Hours from this course may not count toward that license's total.`
          : `You're close to your ${match.label} credit limit for ${stateLabel} (${match.used.toFixed(1)} of ${match.cap} hrs used). Some hours from this course may not count toward that license's total.`}
      </Typography>
    </Box>
  );
}
